/**
 * Comment Posted Notification Component
 * Displays a notification when a comment is posted in a community or ritual
 * Shows message only, no card
 */

import { EllipsisVertical } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getAssetUrl } from "@/utils/assets"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"

interface CommentPostedNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
  onNotificationClick?: (notification: Notification) => void
}

interface CommentPostedNotificationData {
  communityId?: string
  communityName?: string
  ritualId?: string
  ritualName?: string
  entityName?: string
}

export function CommentPostedNotification({
  notification,
  onRead,
  onUnread,
  onNotificationClick,
}: CommentPostedNotificationProps) {
  const data = notification.data as CommentPostedNotificationData
  const triggeredBy = notification.triggeredByUser
  const isRead = notification.isRead

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  const handleUserClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (triggeredBy?.username) {
      window.location.href = `/profile/${triggeredBy.id}`
    }
  }

  const getEntityId = () => {
    return data.ritualId || data.communityId || ""
  }

  const getEntityName = () => {
    return data.ritualName || data.communityName || data.entityName || ""
  }

  const handleEntityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (data.ritualId) {
      window.location.href = `/rituals/${data.ritualId}`
    } else if (data.communityId) {
      window.location.href = `/communities/${data.communityId}`
    }
  }

  const renderMessage = () => {
    const action = notification.action
    const entityName = getEntityName()
    const entityId = getEntityId()

    if (action === "comment_reply") {
      if (triggeredBy) {
        return (
          <>
            <button
              onClick={handleUserClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {triggeredBy.name}
            </button>
            {` replied to your comment in `}
            <button
              onClick={handleEntityClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {entityName}
            </button>
          </>
        )
      }
      return notification.message
    }

    if (action === "comment_posted") {
      if (triggeredBy && entityId && entityName) {
        return (
          <>
            <button
              onClick={handleUserClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {triggeredBy.name || triggeredBy.username}
            </button>
            {" posted a comment in "}
            <button
              onClick={handleEntityClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {entityName}
            </button>
          </>
        )
      }
    }

    if (action === "comment_mention") {
      if (triggeredBy && entityId && entityName) {
        return (
          <>
            <button
              onClick={handleUserClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {triggeredBy.name || triggeredBy.username}
            </button>
            {" mentioned you in a comment in "}
            <button
              onClick={handleEntityClick}
              className="font-semibold cursor-pointer bg-transparent border-none p-0 text-current"
              type="button"
            >
              {entityName}
            </button>
          </>
        )
      }
    }

    return notification.message
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
      onClick={() => {
        onRead?.()
        onNotificationClick?.(notification)
      }}
    >
      {/* Header Row: Logo, Brand, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* Logo */}
          <img
            src={getAssetUrl("Avatar.png")}
            alt="Rituality"
            className="w-12 h-12 rounded-full object-cover"
          />

          {/* Brand Name and Message */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">Rituality</span>
              <span className="text-stone text-[14px]">@Rituality</span>
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">{renderMessage()}</div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  )
}
